import { useState, useEffect } from "react";

const API_URL = process.env.REACT_APP_API_URL;

const RATINGS = [
  { key: "strongBuy",  label: "Strong Buy",  color: "#10b981" },
  { key: "buy",        label: "Buy",          color: "#34d399" },
  { key: "hold",       label: "Hold",         color: "#f59e0b" },
  { key: "sell",       label: "Sell",         color: "#f87171" },
  { key: "strongSell", label: "Strong Sell",  color: "#ef4444" },
];

function getConsensus(d) {
  if (!d) return null;
  const { strongBuy=0, buy=0, hold=0, sell=0, strongSell=0 } = d;
  const total = strongBuy + buy + hold + sell + strongSell;
  if (total === 0) return null;
  const bullPct = (strongBuy + buy) / total;
  const bearPct = (sell + strongSell) / total;
  if (bullPct >= 0.6) return { label: "BULLISH", color: "#10b981", bg: "rgba(16,185,129,0.1)" };
  if (bearPct >= 0.6) return { label: "BEARISH", color: "#ef4444", bg: "rgba(239,68,68,0.1)"  };
  return             { label: "NEUTRAL", color: "#f59e0b", bg: "rgba(245,158,11,0.1)"  };
}

export default function AnalystConsensus({ ticker }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    fetch(`${API_URL}/api/seo/${ticker}/consensus`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((d) => { if (!cancelled) { setData(d.consensus || null); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError("Analyst data unavailable"); setLoading(false); } });
    return () => { cancelled = true; };
  }, [ticker]);

  const consensus = getConsensus(data);
  const total = data ? RATINGS.reduce((s, r) => s + (data[r.key] || 0), 0) : 0;

  return (
    <div style={{ background: "#0f0f23", border: "1px solid #1e1e3a", borderRadius: 12, padding: "20px 24px", marginBottom: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ margin: 0, color: "#e2e8f0", fontSize: 15, fontWeight: 600 }}>Analyst Consensus</h3>
        {consensus && !loading && (
          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", color: consensus.color,
            background: consensus.bg, padding: "3px 10px", borderRadius: 20, border: `1px solid ${consensus.color}40` }}>
            {consensus.label}
          </span>
        )}
      </div>

      {loading && <div style={{ color: "#8b8ba7", fontSize: 14, padding: "16px 0" }}>Loading analyst data…</div>}
      {!loading && error && <div style={{ color: "#8b8ba7", fontSize: 14 }}>{error}</div>}
      {!loading && !error && !data && <div style={{ color: "#8b8ba7", fontSize: 14 }}>No analyst ratings available.</div>}

      {!loading && !error && data && total > 0 && (
        <>
          <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", marginBottom: 16, gap: 1 }}>
            {RATINGS.map(({ key, color }) => {
              const pct = ((data[key] || 0) / total) * 100;
              return pct > 0 ? <div key={key} style={{ width: `${pct}%`, background: color, transition: "width 0.4s" }} /> : null;
            })}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {RATINGS.map(({ key, label, color }) => {
              const count = data[key] || 0;
              const pct   = (count / total) * 100;
              return (
                <div key={key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ width: 90, fontSize: 12, color: "#8b8ba7", flexShrink: 0 }}>{label}</span>
                  <div style={{ flex: 1, background: "#1a1a2e", borderRadius: 4, height: 6, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 4, transition: "width 0.4s ease" }} />
                  </div>
                  <span style={{ width: 24, fontSize: 12, color: "#e2e8f0", textAlign: "right", flexShrink: 0 }}>{count}</span>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 12, fontSize: 11, color: "#8b8ba7" }}>
            {total} analyst{total !== 1 ? "s" : ""} · Most recent period{data.period ? ` · ${data.period}` : ""}
          </div>
        </>
      )}

      <div style={{ marginTop: 10, fontSize: 11, color: "#6b6b8a", borderTop: "1px solid #1e1e3a", paddingTop: 10 }}>
        Analyst ratings are informational only — not investment advice.
      </div>
    </div>
  );
}
